<script lang="ts">
	import { appState } from "$lib/state/app-state.svelte";
	import { onMount } from "svelte";
	import type { DocumentGroup } from "$lib/navigation/document-group";
	import { createIndex, search } from "$lib/navigation/search";
	import type { SearchResult } from "$lib/navigation/search-result";

	interface Props {
		groups: DocumentGroup[];
	}

	const { groups }: Props = $props();

	let results = $state<SearchResult[]>([]);
	let inputRef = $state<HTMLInputElement>();

	onMount(async () => {
		createIndex(groups);

		setTimeout(() => {
			if (inputRef) {
				inputRef.value = appState.searchQuery;
				inputRef?.focus();
				results = search(appState.searchQuery);
			}
		}, 200);
	});

	function onInput(e: Event) {
		const target = e.target as HTMLInputElement;
		appState.searchQuery = target.value;
		results = search(appState.searchQuery);
	}
</script>

<div
	class="fixed inset-0 z-50 block"
	role="dialog"
	tabindex="-1"
	aria-modal="true"
	onkeydown={(e) => {
		if (e.key === "Escape") {
			if (appState.searchQuery) {
				appState.searchQuery = "";
				results = [];
			} else {
				appState.searchVisible = false;
			}
		}
	}}
	onclick={() => (appState.searchVisible = false)}>
	<div class="fixed inset-0 bg-zinc-400/25 backdrop-blur-xs dark:bg-black/40" aria-hidden="true"></div>
	<div class="fixed inset-0 z-10 w-screen overflow-y-auto p-4 sm:p-6 md:p-20">
		<div
			class="mx-auto max-w-2xl transform divide-y divide-gray-500/10 overflow-hidden rounded-xl bg-white/80 shadow-2xl ring-1 ring-black/5 backdrop-blur-sm backdrop-filter transition-all">
			<div class="grid grid-cols-1">
				<input
					type="text"
					class="not-prose col-start-1 row-start-1 h-12 w-full border-none bg-transparent pr-4 pl-11 text-base text-gray-900 outline-hidden placeholder:text-gray-500 focus:ring-0 sm:text-sm"
					placeholder="Hledat..."
					role="combobox"
					aria-expanded="false"
					aria-controls="options"
					bind:this={inputRef}
					oninput={onInput}
					value={appState.searchQuery}
					onclick={(e) => {
						e.preventDefault();
						e.stopPropagation();
					}} />
				<svg
					class="pointer-events-none col-start-1 row-start-1 ml-4 size-5 self-center text-gray-900/40"
					viewBox="0 0 20 20"
					fill="currentColor"
					aria-hidden="true"
					data-slot="icon">
					<path
						fill-rule="evenodd"
						d="M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z"
						clip-rule="evenodd" />
				</svg>
			</div>

			<ul class={["max-h-96 overflow-y-auto p-2 text-sm text-gray-700", results.length > 0 ? "" : "hidden"]}>
				{#each results as result (result.slug)}
					<li
						class={[
							"group flex cursor-default items-center rounded-md text-gray-900 select-none",
							"hover:bg-gray-900/5 hover:text-gray-900 hover:outline-hidden",
						]}>
						<a href={result.slug ? `/${result.slug}/` : `/`} class="w-full px-3 py-2">
							<span class="ml-3 flex-auto truncate">{result.groupTitle} / {result.title}</span>
						</a>
					</li>
				{/each}
			</ul>

			<!-- Empty state, show/hide based on command palette state. -->
			<div
				class={[
					"px-6 py-14 text-center sm:px-14",
					appState.searchQuery === "" || results.length > 0 ? "hidden" : "",
				]}>
				<p class="mt-4 text-sm text-gray-900">Nebyly nalezeny žádné výsledky.</p>
			</div>
		</div>
	</div>
</div>
